import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, Text, IconButton } from '@radix-ui/themes';

import DateEditModal from '../DateEditModal.jsx';
import YearNavigation from './YearNavigation.jsx';
import UnifiedFilterDropdown from './UnifiedFilterDropdown.jsx';
import MonthCard from './MonthCard.jsx';
import MonthDetailView from './MonthDetailView.jsx';
import SectionCard from '../../../../../shared/SectionCard/SectionCard.jsx';
import { useCampaignDateUpdate } from '../useCampaignDateUpdate';
import {
  get12MonthRange,
  STATUS_TYPES,
  STATUS_CONFIG,
  BROADCAST_STATUS_TYPES,
  BROADCAST_STATUS_CONFIG,
  MONTHS,
} from './helpers.js';
import {
  getMonthCardProps,
  isModalOrDropdownClick as checkModalClick,
} from './calendarUtils';
import { useFetchCampaignsByYear } from '../../../hooks/useFetchCampaignsByYear';
import styles from './CalendarView.module.css';

// Highlight status filter items
const HIGHLIGHT_STATUS_ITEMS = [
  {
    value: STATUS_TYPES.DRAFT,
    label: STATUS_CONFIG.draft.label,
    color: STATUS_CONFIG.draft.color,
  },
  {
    value: STATUS_TYPES.SCHEDULED,
    label: STATUS_CONFIG.scheduled.label,
    color: STATUS_CONFIG.scheduled.color,
  },
  {
    value: STATUS_TYPES.ACTIVE,
    label: 'Live',
    color: STATUS_CONFIG.active.color,
  },
  {
    value: STATUS_TYPES.ARCHIVED,
    label: STATUS_CONFIG.archived.label,
    color: STATUS_CONFIG.archived.color,
  },
];

// Broadcast status filter items
const BROADCAST_STATUS_ITEMS = [
  {
    value: BROADCAST_STATUS_TYPES.DRAFT,
    label: BROADCAST_STATUS_CONFIG.draft.label,
    color: BROADCAST_STATUS_CONFIG.draft.color,
  },
  {
    value: BROADCAST_STATUS_TYPES.SCHEDULED,
    label: BROADCAST_STATUS_CONFIG.scheduled.label,
    color: BROADCAST_STATUS_CONFIG.scheduled.color,
  },
  {
    value: BROADCAST_STATUS_TYPES.SENT,
    label: BROADCAST_STATUS_CONFIG.sent.label,
    color: BROADCAST_STATUS_CONFIG.sent.color,
  },
];

export default function CalendarView({
  selectedYearOverride,
  selectedMonthOverride,
  onCalendarNavigate,
  hideBroadcasts = false,
} = {}) {
  const { userType } = useSelector(state => state.currentUser);
  const { t } = useTranslation('campaigns');
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const selectedYear = (() => {
    if (selectedYearOverride !== undefined) return selectedYearOverride;
    const cy = urlParams.get('cy');
    return cy ? Number(cy) : currentYear;
  })();

  const selectedMonth = (() => {
    if (selectedMonthOverride !== undefined) {
      return {
        month: MONTHS[selectedMonthOverride],
        monthIndex: selectedMonthOverride,
      };
    }
    const cm = urlParams.get('cm');
    if (cm !== null && cm !== undefined && cm !== '') {
      const monthIndex = Number(cm);
      return { month: MONTHS[monthIndex], monthIndex };
    }
    return null;
  })();

  const replaceCalendarParams = (year, monthIndex) => {
    if (onCalendarNavigate) {
      onCalendarNavigate(year, monthIndex);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.set('cy', year);
    if (monthIndex !== null && monthIndex !== undefined) {
      params.set('cm', monthIndex);
    } else {
      params.delete('cm');
    }
    history.replace({ search: params.toString() });
  };

  const [modalCampaign, setModalCampaign] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredCampaign, setHoveredCampaign] = useState(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedHighlightFilter, setSelectedHighlightFilter] = useState(() => {
    const hf = urlParams.get('hf');
    return hf ? hf.split(',').filter(Boolean) : [];
  });
  const [selectedBroadcastFilter, setSelectedBroadcastFilter] = useState(() => {
    const bf = urlParams.get('bf');
    return bf ? bf.split(',').filter(Boolean) : [];
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (selectedHighlightFilter.length > 0) {
      params.set('hf', selectedHighlightFilter.join(','));
    } else {
      params.delete('hf');
    }
    if (selectedBroadcastFilter.length > 0) {
      params.set('bf', selectedBroadcastFilter.join(','));
    } else {
      params.delete('bf');
    }

    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, '');
    if (newSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [selectedHighlightFilter, selectedBroadcastFilter]);

  const { campaignsByMonth, broadcastsByMonth } = useFetchCampaignsByYear(
    selectedYear,
  );
  const { updateCampaignDates } = useCampaignDateUpdate();
  const months = get12MonthRange(selectedYear);

  const handleDateUpdate = (campaignId, dates) => {
    updateCampaignDates(campaignId, dates);
  };

  const appendFilterParams = params => {
    if (selectedHighlightFilter.length > 0)
      params.set('hf', selectedHighlightFilter.join(','));
    if (selectedBroadcastFilter.length > 0)
      params.set('bf', selectedBroadcastFilter.join(','));
  };

  const handleCampaignClick = campaign => {
    const params = new URLSearchParams({ from: 'calendar', cy: selectedYear });
    if (selectedMonth) params.set('cm', selectedMonth.monthIndex);
    appendFilterParams(params);
    const isInPlanPage = window.location.pathname.includes('/plan');
    const prefix = isInPlanPage ? '/campaigns' : '';
    history.push(`${prefix}/${campaign.uuid}/edit?${params}`);
  };

  const handleBroadcastClick = broadcast => {
    const params = new URLSearchParams({ from: 'calendar', cy: selectedYear });
    if (selectedMonth) params.set('cm', selectedMonth.monthIndex);
    appendFilterParams(params);
    if (broadcast.status === 'draft') {
      window.location.href = `/client_admin/broadcast_messages#/edit/${broadcast.currentStep ||
        'review'}/${broadcast.uuid}?${params}`;
    } else {
      window.location.href = `/client_admin/broadcast_messages#/${broadcast.uuid}?${params}`;
    }
  };

  const handleDateClick = (event, campaign) => {
    event.stopPropagation();
    setModalCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalCampaign(null);
  };

  const handleArchiveModalChange = isOpen => {
    setIsArchiveModalOpen(isOpen);
  };

  const handleCampaignHover = (campaignId, isHovered) => {
    setHoveredCampaign(isHovered ? campaignId : null);
  };

  const handlePreviousYear = () => {
    replaceCalendarParams(selectedYear - 1, null);
  };

  const handleNextYear = () => {
    replaceCalendarParams(selectedYear + 1, null);
  };

  const handleHighlightFilterChange = filters => {
    setSelectedHighlightFilter(filters);
  };

  const handleBroadcastFilterChange = filters => {
    setSelectedBroadcastFilter(filters);
  };

  const handleMonthClick = ({ monthIndex }) => {
    replaceCalendarParams(selectedYear, monthIndex);
  };

  const handleBackToYear = () => {
    replaceCalendarParams(selectedYear, null);
  };

  const handlePreviousMonth = () => {
    if (selectedMonth.monthIndex === 0) {
      replaceCalendarParams(selectedYear - 1, 11);
    } else {
      replaceCalendarParams(selectedYear, selectedMonth.monthIndex - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth.monthIndex === 11) {
      replaceCalendarParams(selectedYear + 1, 0);
    } else {
      replaceCalendarParams(selectedYear, selectedMonth.monthIndex + 1);
    }
  };

  const isModalOrDropdownClickHandler = event => {
    return checkModalClick(event, isArchiveModalOpen, isModalOpen);
  };

  return (
    <>
      <SectionCard
        contentClassName={styles.calendarContainer}
        headerContent={
          <Flex
            align="center"
            justify="between"
            width="100%"
            style={{ position: 'relative' }}
          >
            {selectedMonth ? (
              <Flex className={styles.yearNavigation} align="center">
                <IconButton
                  className={styles.yearNavigationButton}
                  onClick={handlePreviousMonth}
                  aria-label={t('calendar.previousMonth')}
                  variant="soft"
                  color="gray"
                  size="2"
                >
                  <i className="fa-solid fa-chevron-left" />
                </IconButton>
                <IconButton
                  className={styles.yearNavigationButton}
                  onClick={handleNextMonth}
                  aria-label={t('calendar.nextMonth')}
                  variant="soft"
                  color="gray"
                  size="2"
                >
                  <i className="fa-solid fa-chevron-right" />
                </IconButton>
                <Text className={styles.yearDisplay} size="4" weight="bold">
                  {selectedMonth.month} {selectedYear}
                </Text>
              </Flex>
            ) : (
              <YearNavigation
                selectedYear={selectedYear}
                onPrevious={handlePreviousYear}
                onNext={handleNextYear}
              />
            )}
            <Flex align="center" gap="3">
              <Button
                variant="outline"
                size="2"
                onClick={() => {
                  if (selectedMonth) {
                    handleBackToYear();
                  } else {
                    replaceCalendarParams(currentYear, currentMonth);
                  }
                }}
              >
                {selectedMonth ? t('calendar.month') : t('calendar.year')}
                <i className="fa-solid fa-chevron-down" />
              </Button>
              <UnifiedFilterDropdown
                highlightItems={HIGHLIGHT_STATUS_ITEMS}
                selectedHighlights={selectedHighlightFilter}
                onHighlightChange={handleHighlightFilterChange}
                broadcastItems={hideBroadcasts ? [] : BROADCAST_STATUS_ITEMS}
                selectedBroadcasts={selectedBroadcastFilter}
                onBroadcastChange={handleBroadcastFilterChange}
              />
            </Flex>
          </Flex>
        }
      >
        {selectedMonth ? (
          <MonthDetailView
            month={selectedMonth.month}
            monthIndex={selectedMonth.monthIndex}
            year={selectedYear}
            campaigns={
              // If broadcast filters selected but no highlight filters, hide campaigns
              selectedBroadcastFilter.length > 0 &&
              selectedHighlightFilter.length === 0
                ? []
                : (campaignsByMonth[selectedMonth.month] || []).filter(c => {
                    // When no filters selected, show all
                    if (selectedHighlightFilter.length === 0) return true;
                    // Derive effective status for filtering
                    let effectiveStatus = c.status;
                    if (c.status === 'draft') {
                      if (c.hasLivePost) effectiveStatus = STATUS_TYPES.ACTIVE;
                      else if (c.hasScheduledPost)
                        effectiveStatus = STATUS_TYPES.SCHEDULED;
                    } else if (c.status === 'completed') {
                      effectiveStatus = STATUS_TYPES.ACTIVE;
                    }
                    return selectedHighlightFilter.includes(effectiveStatus);
                  })
            }
            broadcasts={
              hideBroadcasts
                ? []
                : // If highlight filters selected but no broadcast filters, hide broadcasts
                selectedHighlightFilter.length > 0 &&
                  selectedBroadcastFilter.length === 0
                ? []
                : (broadcastsByMonth[selectedMonth.month] || []).filter(
                    b =>
                      // When no filters selected, show all
                      selectedBroadcastFilter.length === 0 ||
                      selectedBroadcastFilter.includes(b.status),
                  )
            }
            onCampaignClick={handleCampaignClick}
            onBroadcastClick={handleBroadcastClick}
          />
        ) : (
          <Box className={styles.calendarBg}>
            <Box className={styles.calendarWrapper}>
              <Box className={styles.calendarGrid}>
                {months.map(({ month, year, key, monthIndex }, index) => {
                  const {
                    isCurrentMonth,
                    filteredCampaigns,
                    isPastMonth,
                    zIndex,
                  } = getMonthCardProps(
                    month,
                    year,
                    monthIndex,
                    index,
                    campaignsByMonth,
                    selectedHighlightFilter,
                    currentYear,
                    currentMonth,
                    months.length,
                    selectedBroadcastFilter,
                  );

                  // Get broadcasts for this month and filter by status
                  const monthBroadcasts = hideBroadcasts
                    ? []
                    : broadcastsByMonth[month] || [];
                  // If highlight filters selected but no broadcast filters, hide broadcasts
                  const filteredBroadcasts = hideBroadcasts
                    ? []
                    : selectedHighlightFilter.length > 0 &&
                      selectedBroadcastFilter.length === 0
                    ? []
                    : selectedBroadcastFilter.length === 0
                    ? monthBroadcasts
                    : monthBroadcasts.filter(broadcast =>
                        selectedBroadcastFilter.includes(broadcast.status),
                      );

                  return (
                    <MonthCard
                      key={key}
                      month={month}
                      monthIndex={monthIndex}
                      year={year}
                      campaigns={filteredCampaigns}
                      broadcasts={filteredBroadcasts}
                      isCurrentMonth={isCurrentMonth}
                      isPastMonth={isPastMonth}
                      zIndex={zIndex}
                      userType={userType}
                      hoveredCampaign={hoveredCampaign}
                      onCampaignHover={handleCampaignHover}
                      onCampaignClick={handleCampaignClick}
                      onBroadcastClick={handleBroadcastClick}
                      onDateClick={handleDateClick}
                      onArchiveModalChange={handleArchiveModalChange}
                      onMonthClick={handleMonthClick}
                      isModalOrDropdownClick={isModalOrDropdownClickHandler}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      <DateEditModal
        isOpen={isModalOpen}
        campaign={modalCampaign}
        onClose={handleModalClose}
        onDateUpdate={handleDateUpdate}
      />
    </>
  );
}
